import { createContext, useCallback, useContext, useEffect, useId, useMemo, useState, type ReactNode } from "react";

/**
 * A compact, pre-formatted summary of the "entity" a detail dialog/sheet
 * currently has open (a Priority or an Objective). Consumed by
 * CopilotAmbientContextBridge to build the ambient useAgentContext string.
 */
export interface OpenEntity {
  description: string;
  value: string;
}

interface CopilotEntityContextValue {
  openEntity: OpenEntity | null;
  /** `entity: null` clears the slot, but only if `id` currently owns it. */
  setOpenEntity: (id: string, entity: OpenEntity | null) => void;
}

export const CopilotEntityContext = createContext<CopilotEntityContextValue | null>(null);

export function CopilotEntityProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<{ id: string; entity: OpenEntity } | null>(null);

  const setOpenEntity = useCallback((id: string, entity: OpenEntity | null) => {
    setCurrent((prev) => {
      if (entity) return { id, entity };
      // Clears are ownership-scoped: several PriorityDetailDialog instances can
      // be mounted at once (page + copilot card), and a closed one must not
      // wipe the entity reported by the one still on screen.
      return prev && prev.id !== id ? prev : null;
    });
  }, []);

  const value = useMemo(
    () => ({ openEntity: current?.entity ?? null, setOpenEntity }),
    [current, setOpenEntity],
  );

  return <CopilotEntityContext.Provider value={value}>{children}</CopilotEntityContext.Provider>;
}

/**
 * Reports the currently open entity (priority/objective detail) to the
 * ambient copilot context. Safe to call unconditionally — before any early
 * return — even from a component not yet wrapped in `CopilotEntityProvider`
 * (no-op with a dev warning in that case, never throws).
 */
export function useSetOpenEntity(entity: OpenEntity | null) {
  const id = useId();
  // `setOpenEntity` is a stable useCallback even though the provider's memoized
  // context value changes identity on every update — depending on the function,
  // not the wrapping object, keeps this effect from re-firing when an unrelated
  // consumer changes the shared entity.
  const setOpenEntity = useContext(CopilotEntityContext)?.setOpenEntity;

  useEffect(() => {
    if (!setOpenEntity) {
      if (import.meta.env.DEV) {
        console.warn(
          "useSetOpenEntity called outside CopilotEntityProvider — open entity will not reach the copilot context.",
        );
      }
      return;
    }

    setOpenEntity(id, entity);

    return () => {
      setOpenEntity(id, null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setOpenEntity, id, entity?.description, entity?.value]);
}
